"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronDown, Copy, Check } from "lucide-react";
import { getTxExplorerUrl } from "@/lib/hoops-contracts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: "info" | "success" | "error" | "warn";
  message: string;
  txHash?: string;
}

export interface TxLogPanelProps {
  logs: LogEntry[];
  defaultOpen?: boolean;
  maxHeight?: number;
}

// ---------------------------------------------------------------------------
// Helper — exported for use in consumers
// ---------------------------------------------------------------------------

export function createLogEntry(
  level: LogEntry["level"],
  message: string,
  txHash?: string,
): LogEntry {
  return {
    id: crypto.randomUUID(),
    timestamp: new Date(),
    level,
    message,
    txHash,
  };
}

// ---------------------------------------------------------------------------
// Level config
// ---------------------------------------------------------------------------

const LEVEL_DOT: Record<LogEntry["level"], string> = {
  info:    "#555",
  success: "#3ecf7e",
  error:   "#e05252",
  warn:    "#e8c96a",
};

const LEVEL_TEXT: Record<LogEntry["level"], string> = {
  info:    "#888",
  success: "#3ecf7e",
  error:   "#e05252",
  warn:    "#e8c96a",
};

function padTwo(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatTime(date: Date): string {
  return `${padTwo(date.getHours())}:${padTwo(date.getMinutes())}:${padTwo(date.getSeconds())}`;
}

function buildPlainText(logs: LogEntry[]): string {
  return logs
    .map((l) => {
      const hash = l.txHash ? ` [tx: ${l.txHash}]` : "";
      return `${formatTime(l.timestamp)} [${l.level.toUpperCase().padEnd(7)}] ${l.message}${hash}`;
    })
    .join("\n");
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TxLogPanel({ logs, defaultOpen = false, maxHeight = 200 }: TxLogPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Auto-expand on error
  useEffect(() => {
    const hasError = logs.some((l) => l.level === "error");
    if (hasError) setOpen(true);
  }, [logs]);

  // Auto-scroll to bottom when new entries arrive (only when open)
  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [logs, open]);

  const handleCopy = useCallback(async () => {
    if (logs.length === 0) return;
    try {
      await navigator.clipboard.writeText(buildPlainText(logs));
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    } catch {
      // clipboard unavailable — silently ignore
    }
  }, [logs]);

  return (
    <div
      style={{
        background: "#0a0a0a",
        border: "1px solid #1c1c1c",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => { setOpen((v) => !v); }}
        role="button"
        aria-expanded={open}
        aria-controls="tx-log-body"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          {/* Tiny terminal cursor indicator */}
          <span
            style={{
              display: "inline-block",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: logs.some((l) => l.level === "error")
                ? "#e05252"
                : logs.some((l) => l.level === "success")
                ? "#3ecf7e"
                : "#444",
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#555",
              fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
            }}
          >
            Transaction Log
          </span>
          {!open && (
            <span
              style={{
                fontSize: "10px",
                color: "#383838",
                fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
              }}
            >
              ({logs.length} {logs.length === 1 ? "entry" : "entries"})
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {/* Copy button — only when expanded */}
          {open && logs.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleCopy();
              }}
              aria-label="Copy log to clipboard"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "3px 7px",
                background: "transparent",
                border: "1px solid #222",
                borderRadius: "4px",
                cursor: "pointer",
                color: copied ? "#3ecf7e" : "#444",
                fontSize: "10px",
                fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
                transition: "color 150ms ease, border-color 150ms ease",
              }}
            >
              {copied ? (
                <Check size={10} aria-hidden="true" />
              ) : (
                <Copy size={10} aria-hidden="true" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          )}

          {/* Chevron */}
          <ChevronDown
            size={13}
            aria-hidden="true"
            style={{
              color: "#444",
              flexShrink: 0,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        </div>
      </div>

      {/* Collapsible body */}
      <div
        id="tx-log-body"
        style={{
          overflow: "hidden",
          maxHeight: open ? `${String(maxHeight)}px` : "0px",
          transition: "max-height 220ms ease",
        }}
      >
        <div
          ref={bodyRef}
          style={{
            overflowY: "auto",
            maxHeight: `${String(maxHeight)}px`,
            padding: "8px 12px 10px",
            borderTop: "1px solid #141414",
          }}
        >
          {logs.length === 0 ? (
            <p
              style={{
                fontSize: "11px",
                color: "#333",
                fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
              }}
            >
              No activity yet
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {logs.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "7px",
                    fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
                    fontSize: "11px",
                    lineHeight: 1.55,
                  }}
                >
                  {/* Timestamp */}
                  <span
                    style={{
                      color: "#3a3a3a",
                      flexShrink: 0,
                      fontSize: "10px",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {formatTime(entry.timestamp)}
                  </span>

                  {/* Level dot */}
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-block",
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: LEVEL_DOT[entry.level],
                      flexShrink: 0,
                      marginBottom: "1px",
                      alignSelf: "center",
                    }}
                  />

                  {/* Message */}
                  <span
                    style={{
                      color: LEVEL_TEXT[entry.level],
                      wordBreak: "break-word",
                      flex: 1,
                    }}
                  >
                    {entry.message}
                  </span>

                  {/* TX hash link */}
                  {entry.txHash && (
                    <a
                      href={getTxExplorerUrl(entry.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View transaction ${entry.txHash.slice(0, 8)}... on Stellar Explorer`}
                      style={{
                        color: "#3ecf7e",
                        textDecoration: "none",
                        flexShrink: 0,
                        fontSize: "10px",
                        opacity: 0.8,
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                        (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.opacity = "0.8";
                        (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none";
                      }}
                    >
                      [tx]
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
